/**
 * @module components/Loading
 * @description Loading wrapper component with delayed visibility.
 * Hides children with CSS transitions until ready state is true, with 300ms delay for smooth UX.
 */

import React, { useState, useEffect } from 'react';
import cx from 'classnames';

const debug = false;

/**
 * @typedef LoadingProps
 * @property {boolean} [ready] - Whether content is ready to display (defaults to true)
 * @property {React.ReactNode} children - Content to show once loaded
 */

/**
 * @component Loading
 * @type {React.FC<LoadingProps>}
 * @summary Loading wrapper that delays visibility with fade-in transition.
 * Waits 300ms before showing children to prevent flash of content during fast loads.
 * @param {LoadingProps} props - Component props
 * @returns {React.ReactElement} Wrapper with conditional loaded class
 */
const Loading = ({ ready, children }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (typeof ready === 'undefined' || ready) {
      setTimeout(() => setLoaded(true), 300);
    }
  }, [ready]);

  debug &&
    console.log(`%cloaded? ${loaded}`, 'background:#bada55; color:#222;');

  return <div className={cx('film__single', { loaded })}>{children}</div>;
};

export default Loading;
